// frontend/src/pages/Dashboard.jsx (v3.0 - Top Navbar Layout)
import React, { useState, useEffect, useRef } from 'react';
import '../styles/Dashboard.css';

function Dashboard({ user, token }) {
  const [moorings, setMoorings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [showCheckInForm, setShowCheckInForm] = useState(false);
  const [selectedMooring, setSelectedMooring] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const [checkInForm, setCheckInForm] = useState({
    boat_type: 'member_boat_1',
    boat_name: '',
    boat_registration: '',
    boat_hailing_port: '',
    departure_date: '',
    notes: ''
  });

  // Photo upload state (post-check-in for unknown boats)
  const [photoUploadMode, setPhotoUploadMode] = useState(null); // { usageId, boatName }
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newlyUploadedPhotos, setNewlyUploadedPhotos] = useState([]);
  const photoInputRef = useRef(null);
  const checkInRef = useRef(null);

  // Departure date change state
  const [changingDeparture, setChangingDeparture] = useState(null); // { usageId, newDate }

  // Existing photo view state
  const [showPhotos, setShowPhotos] = useState({});
  const [photos, setPhotos] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState({});

  // Load moorings on mount
  useEffect(() => {
    fetchMoorings();
    const interval = setInterval(fetchMoorings, 30000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to check-in form when it opens
  useEffect(() => {
    if (showCheckInForm && checkInRef.current) {
      checkInRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCheckInForm]);

  const fetchMoorings = async () => {
    try {
      const response = await fetch('/api/moorings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setMoorings(data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load moorings');
      setLoading(false);
    }
  };

  const openCheckInForm = (mooring) => {
    const defaultBoatName = user.boat1Name || '';
    setCheckInForm({
      boat_type: user.boat1Name ? 'member_boat_1' : 'member_on_other_boat',
      boat_name: defaultBoatName,
      boat_registration: '',
      boat_hailing_port: '',
      departure_date: '',
      notes: ''
    });
    setSelectedMooring({ id: mooring.mooring_id, number: mooring.mooring_number });
    setShowCheckInForm(true);
    setError('');
  };

  const handleBoatTypeChange = (boatType) => {
    let boatName = '';
    if (boatType === 'member_boat_1' && user.boat1Name) {
      boatName = user.boat1Name;
    } else if (boatType === 'member_boat_2' && user.boat2Name) {
      boatName = user.boat2Name;
    }

    setCheckInForm(prev => ({
      ...prev,
      boat_type: boatType,
      boat_name: boatName,
      boat_registration: '',
      boat_hailing_port: ''
    }));
  };

  const handleCheckIn = async () => {
    if (!checkInForm.boat_name || !checkInForm.departure_date) {
      setError('Please enter boat name and departure date');
      return;
    }

    if (checkInForm.boat_type === 'unknown_poaching' &&
        (!checkInForm.boat_registration || !checkInForm.boat_hailing_port)) {
      setError('Registration and hailing port required for unknown boats');
      return;
    }

    try {
      const response = await fetch('/api/usage/check-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mooring_id: selectedMooring.id,
          ...checkInForm
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Check-in failed');
        return;
      }

      // For unknown boats, go to photo upload step instead of closing
      if (checkInForm.boat_type === 'unknown_poaching' && data.usage?.id) {
        setShowCheckInForm(false);
        setSelectedMooring(null);
        setNewlyUploadedPhotos([]);
        setPhotoUploadMode({ usageId: data.usage.id, boatName: checkInForm.boat_name });
        setCheckInForm({
          boat_type: 'member_boat_1',
          boat_name: '',
          boat_registration: '',
          boat_hailing_port: '',
          departure_date: '',
          notes: ''
        });
        fetchMoorings();
        return;
      }

      setMessage(
        data.flagged
          ? `⚠️ ${data.flagged} - ${checkInForm.boat_name} logged!`
          : `✅ ${checkInForm.boat_name} checked in!`
      );

      setShowCheckInForm(false);
      setSelectedMooring(null);
      setCheckInForm({
        boat_type: 'member_boat_1',
        boat_name: '',
        boat_registration: '',
        boat_hailing_port: '',
        departure_date: '',
        notes: ''
      });

      setTimeout(() => setMessage(''), 4000);
      fetchMoorings();
    } catch (err) {
      console.error('Check-in error:', err);
      setError('Connection error');
    }
  };

  const handleCheckOut = async (usageId) => {
    try {
      const response = await fetch(`/api/usage/${usageId}/check-out`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Check-out failed');
        return;
      }

      setMessage('✅ Boat checked out!');
      setTimeout(() => setMessage(''), 3000);
      fetchMoorings();
    } catch (err) {
      console.error('Check-out error:', err);
      setError('Connection error');
    }
  };

  // Save an updated departure date for an occupied mooring
  const handleSaveDeparture = async (usageId, newDate) => {
    if (!newDate) {
      setError('Please select a new departure date');
      return;
    }
    try {
      const response = await fetch(`/api/usage/${usageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ departure_date: newDate })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Could not update departure date');
        return;
      }

      setChangingDeparture(null);
      setMessage('✅ Departure date updated!');
      setTimeout(() => setMessage(''), 3000);
      fetchMoorings();
    } catch (err) {
      console.error('Departure update error:', err);
      setError('Connection error');
    }
  };

  // Upload a photo for a mooring usage record
  const handlePhotoUpload = async (usageId, file, onSuccess) => {
    if (!file) return;

    setUploadingPhotos(prev => ({ ...prev, [usageId]: true }));
    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`/api/usage/${usageId}/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Photo upload failed');
        setUploadingPhotos(prev => ({ ...prev, [usageId]: false }));
        setUploadingPhoto(false);
        return;
      }

      if (onSuccess) onSuccess(data.photo);
      fetchPhotos(usageId);
      setMessage('✅ Photo uploaded!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Photo upload error:', err);
      setError('Photo upload failed');
    }
    setUploadingPhotos(prev => ({ ...prev, [usageId]: false }));
    setUploadingPhoto(false);
  };

  const fetchPhotos = async (usageId) => {
    try {
      const response = await fetch(`/api/usage/${usageId}/photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPhotos(prev => ({ ...prev, [usageId]: data }));
    } catch (err) {
      console.error('Fetch photos error:', err);
    }
  };

  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const getMaxDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  if (loading) return <div className="loading">Loading moorings...</div>;

  const groupedMoorings = {};
  moorings.forEach(mooring => {
    if (!groupedMoorings[mooring.harbor_name]) {
      groupedMoorings[mooring.harbor_name] = [];
    }
    groupedMoorings[mooring.harbor_name].push(mooring);
  });

  return (
    <div className="dashboard v3">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>🌊 Mooring Dashboard</h2>
          <p>View all boats and manage your mooring</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-sm help-btn" onClick={() => setShowHelp(!showHelp)}>
            {showHelp ? '✕ Close Help' : 'ℹ️ How to Use'}
          </button>
        </div>
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="help-panel">
          <h3>📖 How to Use the Mooring System</h3>

          <div className="help-section">
            <h4>Checking In a Boat</h4>
            <p>1. Find an available mooring (green card showing "Available").</p>
            <p>2. Click the <strong>"📌 Check In Boat"</strong> button on that mooring.</p>
            <p>3. Select the boat type:</p>
            <p>&nbsp;&nbsp;• <strong>My Boat 1 / Boat 2</strong> — your registered boat (name fills in automatically).</p>
            <p>&nbsp;&nbsp;• <strong>Another member's boat (club burgee)</strong> — a boat belonging to another club member.</p>
            <p>&nbsp;&nbsp;• <strong>I'm aboard this boat (my burgee)</strong> — you are on the boat but it's not your registered boat. You're flying your burgee.</p>
            <p>&nbsp;&nbsp;• <strong>Unknown boat</strong> — not in the club system (possible poaching). Registration and hailing port required. You'll be prompted to upload photos.</p>
            <p>4. Pick a departure date (today through 7 days from now).</p>
            <p>5. Click <strong>"✅ Check In"</strong>.</p>
          </div>

          <div className="help-section">
            <h4>Changing a Departure Date</h4>
            <p>On any occupied mooring card, click <strong>"📅 Change Departure"</strong> to update the expected departure date. The date must still be within 7 days from today.</p>
          </div>

          <div className="help-section">
            <h4>Checking Out a Boat</h4>
            <p>Find the occupied mooring and click <strong>"🏃 Check Out"</strong>. Any member can check out any boat.</p>
          </div>

          <div className="help-section">
            <h4>Understanding the Colors</h4>
            <p>• <span style={{color: '#4caf50', fontWeight: 'bold'}}>Green</span> — Available.</p>
            <p>• <span style={{color: '#ff9800', fontWeight: 'bold'}}>Orange</span> — Occupied.</p>
            <p>• <span style={{color: '#dc3545', fontWeight: 'bold'}}>Red badge (⚠️ POACHING)</span> — Unknown boat not in system.</p>
            <p>• <span style={{color: '#ff9800', fontWeight: 'bold'}}>Orange badge (🚩 CLUB BURGEE)</span> — Member on another member's boat.</p>
            <p>• <span style={{color: '#0066cc', fontWeight: 'bold'}}>Blue badge (🚩 MY BURGEE)</span> — Member aboard a non-registered boat.</p>
          </div>

          <div className="help-section">
            <h4>Reports</h4>
            <p>Click <strong>"📊 Reports"</strong> in the top navigation bar to view mooring status, poaching incidents, and burgee activity. Reports can be exported as CSV files.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}
      {message && <div className="alert alert-success">{message}</div>}

      {/* Check-in form panel — appears above the mooring grid */}
      {showCheckInForm && selectedMooring && (
        <div className="check-in-panel" ref={checkInRef}>
          <h3>Check In Boat to {selectedMooring.number}</h3>

          <div className="check-in-body">
            <div className="boat-type-selector">
              <h4>What boat are you using?</h4>

              <div className="radio-group">
                {user.boat1Name && (
                  <label>
                    <input
                      type="radio"
                      value="member_boat_1"
                      checked={checkInForm.boat_type === 'member_boat_1'}
                      onChange={(e) => handleBoatTypeChange(e.target.value)}
                    />
                    My Boat 1: {user.boat1Name}
                  </label>
                )}

                {user.boat2Name && (
                  <label>
                    <input
                      type="radio"
                      value="member_boat_2"
                      checked={checkInForm.boat_type === 'member_boat_2'}
                      onChange={(e) => handleBoatTypeChange(e.target.value)}
                    />
                    My Boat 2: {user.boat2Name}
                  </label>
                )}

                <label>
                  <input
                    type="radio"
                    value="member_on_other_boat"
                    checked={checkInForm.boat_type === 'member_on_other_boat'}
                    onChange={(e) => handleBoatTypeChange(e.target.value)}
                  />
                  🚩 Another member's boat (club burgee)
                </label>

                <label>
                  <input
                    type="radio"
                    value="member_on_my_burgee"
                    checked={checkInForm.boat_type === 'member_on_my_burgee'}
                    onChange={(e) => handleBoatTypeChange(e.target.value)}
                  />
                  🚩 I'm aboard this boat (flying my burgee)
                </label>

                <label>
                  <input
                    type="radio"
                    value="unknown_poaching"
                    checked={checkInForm.boat_type === 'unknown_poaching'}
                    onChange={(e) => handleBoatTypeChange(e.target.value)}
                  />
                  ⚠️ Unknown boat (not flying a burgee)
                </label>
              </div>
            </div>

            <div className="check-in-fields">
              {checkInForm.boat_type === 'member_on_my_burgee' && (
                <div className="info-box info-box-blue">
                  <p>🚩 You are aboard this boat and flying your club burgee, but the boat is not in your registered boat list.</p>
                </div>
              )}

              <div className="form-group">
                <label>Boat Name:</label>
                <input
                  type="text"
                  value={checkInForm.boat_name}
                  onChange={(e) => setCheckInForm({ ...checkInForm, boat_name: e.target.value })}
                  placeholder="Enter boat name"
                  readOnly={checkInForm.boat_type === 'member_boat_1' || checkInForm.boat_type === 'member_boat_2'}
                />
              </div>

              {checkInForm.boat_type === 'unknown_poaching' && (
                <>
                  <div className="form-group">
                    <label>Registration Number: *</label>
                    <input
                      type="text"
                      value={checkInForm.boat_registration}
                      onChange={(e) => setCheckInForm({ ...checkInForm, boat_registration: e.target.value })}
                      placeholder="e.g., ABC-1234"
                    />
                  </div>

                  <div className="form-group">
                    <label>Hailing Port: *</label>
                    <input
                      type="text"
                      value={checkInForm.boat_hailing_port}
                      onChange={(e) => setCheckInForm({ ...checkInForm, boat_hailing_port: e.target.value })}
                      placeholder="e.g., Newport, RI"
                    />
                  </div>

                  <div className="info-box">
                    <p>⚠️ This boat will be flagged as POACHING/UNKNOWN.</p>
                    <p>After check-in you'll be prompted to add photos.</p>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Expected Departure Date:</label>
                <input
                  type="date"
                  min={getTodayDate()}
                  max={getMaxDate()}
                  value={checkInForm.departure_date}
                  onChange={(e) => setCheckInForm({ ...checkInForm, departure_date: e.target.value })}
                />
                <small>Today through 7 days from now</small>
              </div>

              <div className="form-group">
                <label>Notes (optional):</label>
                <textarea
                  value={checkInForm.notes}
                  onChange={(e) => setCheckInForm({ ...checkInForm, notes: e.target.value })}
                  placeholder="e.g., 'On John's boat', 'Observed at 3pm'"
                  rows="2"
                />
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleCheckIn}>
                  ✅ Check In
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCheckInForm(false);
                    setSelectedMooring(null);
                  }}
                >
                  ❌ Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo upload panel - shown after checking in an unknown boat */}
      {photoUploadMode && (
        <div className="check-in-panel photo-upload-panel">
          <div className="photo-upload-header">
            <span className="badge danger">⚠️ POACHING</span>
            <h3>{photoUploadMode.boatName} — Add Photos</h3>
            <p className="photo-upload-subtitle">
              Boat logged successfully. Add photos of the vessel as evidence (optional).
            </p>
          </div>

          {newlyUploadedPhotos.length > 0 && (
            <div className="photos-gallery uploaded-gallery">
              {newlyUploadedPhotos.map((photo, i) => (
                <a key={i} href={photo.photo_url} target="_blank" rel="noopener noreferrer">
                  <img src={photo.photo_url} alt="Boat" className="photo-thumb" />
                </a>
              ))}
            </div>
          )}

          <div className="photo-upload-area">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files[0]) {
                  handlePhotoUpload(
                    photoUploadMode.usageId,
                    e.target.files[0],
                    (photo) => setNewlyUploadedPhotos(prev => [...prev, photo])
                  );
                  e.target.value = '';
                }
              }}
            />
            <button
              className="btn btn-primary"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
            >
              {uploadingPhoto ? '⏳ Uploading...' : '📸 Choose Photo'}
            </button>
            <small>You can upload multiple photos. Tap "Choose Photo" for each one.</small>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setPhotoUploadMode(null);
                setNewlyUploadedPhotos([]);
                setMessage(`⚠️ Unknown boat "${photoUploadMode.boatName}" logged!`);
                setTimeout(() => setMessage(''), 4000);
              }}
            >
              ✓ Done
            </button>
          </div>
        </div>
      )}

      {/* Mooring grid — full width, no side panel */}
      <div className="moorings-list">
        {Object.entries(groupedMoorings).map(([harbor, mooringList]) => (
          <div key={harbor} className="harbor-section">
            <h3>{harbor}</h3>
            <div className="moorings-grid">
              {mooringList.map(mooring => (
                <div
                  key={mooring.mooring_id}
                  className={`mooring-card ${mooring.is_active ? 'has-boats' : 'available'}`}
                >
                  <div className="mooring-header">
                    <h4>{mooring.mooring_number}</h4>
                  </div>

                  {mooring.is_active ? (
                    <div className="mooring-boats">
                      <div className={`boat-entry boat-${mooring.boat_type}`}>
                        {mooring.boat_type === 'unknown_poaching' && (
                          <span className="badge danger">⚠️ POACHING</span>
                        )}
                        {mooring.boat_type === 'member_on_other_boat' && (
                          <span className="badge warning">🚩 CLUB BURGEE</span>
                        )}
                        {mooring.boat_type === 'member_on_my_burgee' && (
                          <span className="badge info">🚩 MY BURGEE</span>
                        )}

                        <div className="boat-info">
                          <p><strong>Boat:</strong> {mooring.boat_name}</p>

                          {mooring.member_name && (
                            <p><strong>Member:</strong> {mooring.member_name}</p>
                          )}

                          {mooring.boat_registration && (
                            <p><strong>Registration:</strong> {mooring.boat_registration}</p>
                          )}

                          {mooring.boat_hailing_port && (
                            <p><strong>Port:</strong> {mooring.boat_hailing_port}</p>
                          )}

                          {/* Departure date display with change option */}
                          {changingDeparture?.usageId === mooring.usage_id ? (
                            <div className="departure-change-inline">
                              <label>New departure date:</label>
                              <input
                                type="date"
                                min={getTodayDate()}
                                max={getMaxDate()}
                                value={changingDeparture.newDate}
                                onChange={(e) =>
                                  setChangingDeparture(prev => ({ ...prev, newDate: e.target.value }))
                                }
                              />
                              <div className="departure-change-actions">
                                <button
                                  className="btn btn-xs btn-primary"
                                  onClick={() => handleSaveDeparture(mooring.usage_id, changingDeparture.newDate)}
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-xs btn-secondary"
                                  onClick={() => setChangingDeparture(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p>
                              <strong>Departs:</strong> {mooring.departure_date}
                              <button
                                className="change-date-link"
                                onClick={() => setChangingDeparture({ usageId: mooring.usage_id, newDate: mooring.departure_date })}
                              >
                                📅 Change
                              </button>
                            </p>
                          )}
                        </div>

                        {/* Photo gallery for existing photos */}
                        {mooring.photo_count > 0 && (
                          <div className="boat-photos">
                            <button
                              className="view-photos-btn"
                              onClick={() => {
                                setShowPhotos(prev => ({
                                  ...prev,
                                  [mooring.usage_id]: !prev[mooring.usage_id]
                                }));
                                if (!photos[mooring.usage_id]) {
                                  fetchPhotos(mooring.usage_id);
                                }
                              }}
                            >
                              📸 {mooring.photo_count} Photo{mooring.photo_count > 1 ? 's' : ''}
                            </button>

                            {showPhotos[mooring.usage_id] && photos[mooring.usage_id] && (
                              <div className="photos-gallery">
                                {photos[mooring.usage_id].map(photo => (
                                  <a key={photo.id} href={photo.photo_url} target="_blank" rel="noopener noreferrer">
                                    <img src={photo.photo_url} alt="Boat" className="photo-thumb" />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="boat-actions">
                          {/* Add photo button for unknown/poaching boats on existing cards */}
                          {mooring.boat_type === 'unknown_poaching' && (
                            <label className="btn btn-sm btn-photo-upload">
                              📸 Add Photo
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handlePhotoUpload(mooring.usage_id, e.target.files[0]);
                                  }
                                }}
                              />
                            </label>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleCheckOut(mooring.usage_id)}
                          >
                            🏃 Check Out
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mooring-empty">
                      <p>Available</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => openCheckInForm(mooring)}
                      >
                        📌 Check In Boat
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Dashboard;
